import {
  endsWith,
  findIndex,
  orderBy,
  startsWith,
  uniq,
  last,
  size,
} from 'lodash';

const findCurrentSuffixes = (files, defaultName) =>
  files
    .filter(({ name }) => startsWith(name, defaultName))
    .map(({ name }) => {
      const suffix = name
        .slice(defaultName.length)
        .trim()
        .replace(/[()]/g, '');
      return !suffix ? 0 : parseInt(suffix, 10);
    })
    .filter(n => !Number.isNaN(n))
    .sort((a, b) => a - b);

const calculateNextSuffix = suffixes => {
  // Set as nextSuffix the upper limit. For example if the currentSuffixes are
  // [0,1,2,3] the nextSuffix is the upper limit + 1. If there are no suffixes
  // then the nextSuffix is the
  let nextSuffix = size(suffixes) === 0 ? 0 : last(suffixes) + 1;

  // If the default name is missing then
  // the suffix should be 0
  if (suffixes[0] !== 0) {
    nextSuffix = 0;
  } else if (size(suffixes) > 1) {
    // If we have more than one suffixes then we
    // check if there is a gap between the suffixes. For example
    // [0,1,4,5]. The gap is between 1 and 4. So the nextSuffix should
    // be the upper limit of the first suffix that has the gap, in the example
    // the upper limit of 1
    for (let i = 1; i < size(suffixes); i += 1) {
      const gap = suffixes[i] - suffixes[i - 1];

      if (gap > 1) {
        nextSuffix = suffixes[i - 1] + 1;
        break;
      }
    }
  }

  return nextSuffix;
};

export const nameNewFile = (files, defaultName) => {
  const currentSuffixes = findCurrentSuffixes(files, defaultName);
  const uniqCurrentSuffixes = uniq(currentSuffixes);
  const nextSuffix = calculateNextSuffix(uniqCurrentSuffixes);

  return `${defaultName}${nextSuffix > 0 ? ` (${nextSuffix})` : ''}`;
};

export const getProgressPercentage = progress =>
  Math.floor((progress.loaded * 100) / progress.total);

export const updateUploadingFiles = (files, file, changes) =>
  files.map(f =>
    f.name === file.name
      ? {
          name: file.name,
          size: file.size,
          ...changes,
        }
      : f,
  );

export const fileIsAlreadyOpened = (files, file) =>
  files.findIndex(f => f.name === file.name) !== -1;

export const calculateDownloadedTestSetName = name =>
  endsWith(name, '.txt') ? name : `${name}.txt`;

export const getNormalisedFiles = (data, filesMap, genId) =>
  data.map(file =>
    filesMap[file.path]
      ? {
          ...filesMap[file.path],
          ...file,
        }
      : {
          ...file,
          id: genId(),
        },
  );

export const getNormalisedTestSets = (data, filesMap) =>
  data.map(file => ({
    ...filesMap[file.id],
    ...file,
  }));

export const getOpenedFiles = files =>
  orderBy(files.filter(file => file.openedAt), ['openedAt'], ['desc']);

export const getNextPresentedFileId = (curId, deletedIds, files) => {
  if (!deletedIds.includes(curId)) {
    return curId;
  }

  // if the presentedId is one of the deleted ids
  // we should find the next file and set its id to presentedId
  // Next file to open is the file with the
  // closest index to the current presented file
  const presentedList = orderBy(
    files.filter(file => file.openedAt),
    ['openedAt'],
    ['desc'],
  );

  const currIndex = findIndex(presentedList, {
    id: curId,
  });

  // Default value is null
  // We select one by one all smaller indexes
  // If we surpass the current index we select the very next
  return presentedList.reduce((acc, file, i) => {
    if (
      !deletedIds.includes(file.id) &&
      (i < currIndex || (i > currIndex && acc === null))
    ) {
      return file.id;
    }

    return acc;
  }, null);
};
